import React, { useState, useEffect, useCallback } from 'react';
import { rdvAdminAPI } from '../services/api';
import { getSocket } from '../services/socket';

// ─────────────────────────────────────────────────────────────────────────────
// Rendez-vous (admin) — the appointments grid. Single source of truth that
// replaced the advisors' Google Sheets: the calling script auto-creates rows
// on "RDV confirmé". Outcomes: Rencontre 🤝 / No show 🚫 / Injoignable 📵
// (agent rappelle) / 2ᵉ RDV 📅 (client rappelle) / Annulé ✗ (erreur).
// ─────────────────────────────────────────────────────────────────────────────

const STATUTS = {
  nouveau:   { label: 'Nouveau',     color: '#2563eb', bg: 'rgba(37,99,235,0.12)' },
  confirme:  { label: 'Confirmé',     color: '#10b981', bg: 'rgba(16,185,129,0.12)' },
  annule:    { label: 'Annulé',       color: '#ef4444', bg: 'rgba(239,68,68,0.10)' },
  rencontre: { label: 'OK — rencontre', color: '#3b82f6', bg: 'rgba(59,130,246,0.10)' },
  no_show:   { label: 'No show',      color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  injoignable:{ label: 'Injoignable', color: '#f97316', bg: 'rgba(249,115,22,0.12)' },
  rdv2:      { label: '2ᵉ RDV',       color: '#a855f7', bg: 'rgba(168,85,247,0.12)' },
  refus:     { label: 'Refusé',       color: '#64748b', bg: 'rgba(100,116,139,0.12)' },
};

function fmtDate(d) {
  if (!d) return '—';
  try {
    const dt = new Date(d.length === 10 ? d + 'T12:00:00' : d);
    return dt.toLocaleDateString('fr-CA', { day: '2-digit', month: 'short', weekday: 'short' });
  } catch { return d; }
}
function fmtTime(t) { return t ? String(t).slice(0, 5) : '—'; }
function fmtDT(dt) {
  if (!dt) return '';
  try { return new Date(dt).toLocaleString('fr-CA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return dt; }
}

const PERIODES = [
  { key: 'today',    label: "Aujourd'hui" },
  { key: 'week',     label: 'Cette semaine' },
  { key: 'upcoming', label: 'À venir' },
  { key: 'all',      label: 'Tout' },
  { key: 'followup', label: 'Rappels dus' },
];

const inputStyle = { padding: '7px 9px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13, width: '100%', background: 'var(--bg-card)' };

// ── Réponses SMS (admin/supervisor ONLY) ─────────────────────────────────────
// Inbound SMS from prospects (picked up from the TextFree inbox by the
// backend poller). The admin triages and calls back — agents never see these.
function SmsReplies() {
  const [replies, setReplies] = useState([]);
  const [open, setOpen] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await rdvAdminAPI.replies();
      setReplies(Array.isArray(data) ? data : []);
    } catch { setReplies([]); }
  }, []);
  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    // Karim 2026-10-20 : un RDV généré par le script rafraîchit la grille en
    // direct (le ping advisors:refresh part désormais de /rdv/book aussi).
    const s = getSocket();
    const onRefresh = () => load();
    if (s) s.on('advisors:refresh', onRefresh);
    return () => { clearInterval(iv); if (s) s.off('advisors:refresh', onRefresh); };
  }, [load]);

  const toggleHandled = async (r) => {
    try { await rdvAdminAPI.replyHandled(r.id, !r.handled); load(); } catch { }
  };

  const pending = replies.filter(r => !r.handled);
  if (replies.length === 0) return null;

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginBottom: 16, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen(o => !o)}>
        <span style={{ fontSize: 14 }}>📱</span>
        <strong style={{ fontSize: 13 }}>Réponses SMS</strong>
        {pending.length > 0 && (
          <span style={{ fontSize: 11, fontWeight: 800, color: '#f59e0b', background: 'rgba(245,158,11,0.15)', border: '1px solid #f59e0b', padding: '1px 8px', borderRadius: 999 }}>
            {pending.length} à traiter
          </span>
        )}
        <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 'auto' }}>
          Réponses des prospects — visible admin/superviseur uniquement · {open ? '▾' : '▸'}
        </span>
      </div>
      {open && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ background: 'var(--bg-secondary)', textAlign: 'left' }}>
              {['Reçu', 'De', 'Message', 'RDV lié', 'Agent', 'Statut', 'Action'].map(h => (
                <th key={h} style={{ padding: '7px 12px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {replies.map(r => (
              <tr key={r.id} style={{ borderTop: '1px solid var(--border)', background: r.handled ? 'transparent' : 'rgba(245,158,11,0.05)' }}>
                <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', color: 'var(--text-secondary)', fontSize: 11.5 }}>{fmtDT(r.created_at)}</td>
                <td style={{ padding: '8px 12px', fontFamily: 'var(--mono)', fontSize: 11.5 }}>{r.from_phone}{' '}
                  {r.client_first || r.client_last ? <span style={{ fontWeight: 700 }}>({[r.client_first, r.client_last].filter(Boolean).join(' ')})</span> : <span style={{ color: 'var(--text-muted)', fontSize: 10.5 }}>(aucun RDV lié)</span>}
                </td>
                <td style={{ padding: '8px 12px', maxWidth: 320 }}>{r.message || '—'}</td>
                <td style={{ padding: '8px 12px', fontSize: 11.5 }}>
                  {r.rdv_id ? (
                    <>{fmtDate(r.rdv_date)} à {fmtTime(r.rdv_time)} · {r.advisor_name || '—'}</>
                  ) : '—'}
                </td>
                <td style={{ padding: '8px 12px', fontSize: 11.5, color: 'var(--text-secondary)' }}>{r.agent_name || '—'}</td>
                <td style={{ padding: '8px 12px', fontSize: 11.5, color: 'var(--text-secondary)' }}>
                  {r.rdv_statut ? (STATUTS[r.rdv_statut]?.label || r.rdv_statut) : '—'}
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                  <button type="button" className={r.handled ? 'btn btn-secondary btn-sm' : 'btn btn-success btn-sm'}
                    onClick={() => toggleHandled(r)}>
                    {r.handled ? '↺ Réouvrir' : '✓ Traité (rappelé)'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function Rendezvous() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [statut, setStatut] = useState('');
  const [q, setQ] = useState('');
  const [advisors, setAdvisors] = useState([]);
  const [advisorId, setAdvisorId] = useState('');
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (period === 'today') params.period = 'today';
      else if (period === 'week') params.period = 'week';
      else if (period === 'upcoming') params.period = 'upcoming';
      else if (period === 'followup') params.followup = 'today';
      if (advisorId) params.advisor_id = advisorId;
      if (statut) params.statut = statut;
      if (q.trim()) params.q = q.trim();
      const data = await rdvAdminAPI.list(params);
      setRows(Array.isArray(data) ? data : []);
      setError('');
    } catch (e) {
      setError(e.error || 'Failed to load rendez-vous');
      setRows([]);
    } finally { setLoading(false); }
  }, [period, statut, q, advisorId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { rdvAdminAPI.advisors().then(d => setAdvisors(Array.isArray(d) ? d : [])).catch(() => {}); }, []);

  const markRetour = async (r, newStatut) => {
    let followup_at = null;
    let retour = r.retour || '';
    let payload = { statut: newStatut, retour, followup_at };
    if (newStatut === 'injoignable' || newStatut === 'rdv2') {
      const d = window.prompt(newStatut === 'injoignable'
        ? "Date du rappel par l'AGENT (AAAA-MM-JJ HH:MM, heure du Québec) :"
        : 'Date du rappel par le CLIENT — 2ᵉ RDV (AAAA-MM-JJ HH:MM) :', '');
      if (!d) return;
      followup_at = d;
      payload.followup_at = d;
      retour = window.prompt('Note (optionnel) :', retour) ?? retour;
      payload.retour = retour;
    } else if (newStatut === 'reporte') {
      const nd = window.prompt('RDV reporté — NOUVELLE date du rendez-vous (AAAA-MM-JJ) :', '');
      if (!nd) return;
      const nt = window.prompt('NOUVELLE heure (HH:MM) :', '');
      if (!nt) return;
      const note = window.prompt('Note (optionnel) :', retour) ?? retour;
      payload = { statut: 'reporte', retour: note, new_rdv_date: nd, new_rdv_time: nt };
    } else if (newStatut === 'no_show' || newStatut === 'rencontre' || newStatut === 'refus') {
      const msg = newStatut === 'rencontre' ? 'Résultat de la rencontre (optionnel) :'
        : newStatut === 'no_show' ? 'No show — note (optionnel) :'
        : 'Refus — note (optionnel) :';
      const note = window.prompt(msg, retour);
      if (note === null) return;
      retour = note;
      payload.retour = note;
    }
    try { await rdvAdminAPI.retour(r.id, payload); load(); }
    catch (e) { setError(e.error || 'Update failed'); }
  };

  const cancelError = async (r) => {
    if (!window.confirm(`Annuler le rendez-vous de ${r.client_first || ''} ${r.client_last || ''} (${fmtDate(r.rdv_date)}) ?`)) return;
    try { await rdvAdminAPI.cancelError(r.id); load(); } catch (e) { setError(e.error || 'Cancel failed'); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Supprimer DÉFINITIVEMENT le rendez-vous de ${r.client_first || ''} ${r.client_last || ''} ?`)) return;
    try { await rdvAdminAPI.remove(r.id); load(); } catch (e) { setError(e.error || 'Delete failed'); }
  };

  const saveEdit = async () => {
    setSaving(true); setError('');
    try {
      const b = edit;
      const payload = {
        advisor_id: b.advisor_id, client_first: b.client_first, client_last: b.client_last,
        phone: b.phone, email: b.email, ligne2: b.ligne2,
        rdv_date: b.rdv_date, rdv_time: b.rdv_time, mode: b.mode,
        statut: b.statut || 'nouveau', comments: b.comments, retour: b.retour,
        followup_at: b.followup_at || null,
      };
      if (b.id) await rdvAdminAPI.update(b.id, payload);
      else await rdvAdminAPI.create(payload);
      setEdit(null); load();
    } catch (e) { setError(e.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  // « Données collectées » — the call-time recap. Single source (2026-10-20):
  // the summary lives in rendezvous.comments (what advisors read); the legacy
  // script_summary key on the lead is the fallback for RDVs booked before.
  const collectedData = (r) => {
    if (r.comments) return r.comments;
    try {
      const cf = typeof r.lead_custom_fields === 'string'
        ? JSON.parse(r.lead_custom_fields || '{}') : (r.lead_custom_fields || {});
      return cf.script_summary || null;
    } catch { return null; }
  };
  const [detail, setDetail] = useState(null);

  const badge = (s) => {
    const m = STATUTS[s] || STATUTS.nouveau;
    return <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 800, color: m.color, background: m.bg, border: `1.5px solid ${m.color}` }}>{m.label}</span>;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 900, margin: 0 }}>Rendez-vous</h1>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 4 }}>
            Tous les rendez-vous — créés automatiquement quand un agent confirme un RDV dans le script. Remplace le spreadsheet.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={load}>↻ Refresh</button>
          <button type="button" className="btn btn-primary" onClick={() => setEdit({ mode: 'telephonique', statut: 'nouveau' })}>+ Add</button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid var(--danger)', borderRadius: 'var(--radius)', color: 'var(--danger)', fontSize: 13, marginBottom: 14 }}>
          {error}
        </div>
      )}

      <SmsReplies />

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        {PERIODES.map(p => (
          <button type="button" key={p.key} onClick={() => setPeriod(p.key)} className={period === p.key ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>{p.label}</button>
        ))}
        <select className="input" style={{ width: 170, padding: '6px 8px', fontSize: 12 }} value={advisorId} onChange={e => setAdvisorId(e.target.value)}>
          <option value="">Tous les clients</option>
          {advisors.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select className="input" style={{ width: 150, padding: '6px 8px', fontSize: 12 }} value={statut} onChange={e => setStatut(e.target.value)}>
          <option value="">Tous statuts</option>
          {Object.entries(STATUTS).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        <input className="input" style={{ width: 200, padding: '6px 10px', fontSize: 12 }} value={q} placeholder="Rechercher nom / tel…" onChange={e => setQ(e.target.value)} />
      </div>

      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'auto' }}>
        {loading ? (
          <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: 40 }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 8 }}>Aucun rendez-vous pour ce filtre.</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>Changez les filtres ci-dessus.</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 980 }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)', textAlign: 'left' }}>
                {['Appel', 'Agent', 'Prospect', 'Téléphone', 'Courriel', 'RDV', 'Heure', 'Client', 'Mode', 'Statut', 'Commentaires / suivi', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '9px 12px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const due = r.followup_at && new Date(r.followup_at) <= new Date() && (r.statut === 'injoignable' || r.statut === 'rdv2');
                const collected = collectedData(r);
                return (
                  <tr key={r.id} style={{ borderTop: '1px solid var(--border)', background: r.statut === 'annule' ? 'rgba(239,68,68,0.05)' : due ? 'rgba(249,115,22,0.06)' : 'transparent' }}>
                    <td style={{ padding: '8px 12px', fontSize: 11.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{r.created_at ? fmtDT(r.created_at) : '—'}</td>
                    <td style={{ padding: '8px 12px', fontSize: 11.5, color: 'var(--text-secondary)' }}>{r.agent_name || r.agent_username || '—'}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 700 }}>{[r.client_first, r.client_last].filter(Boolean).join(' ') || '—'}</td>
                    <td style={{ padding: '8px 12px', fontFamily: 'var(--mono)' }}>{r.phone || '—'}</td>
                    <td style={{ padding: '8px 12px', fontSize: 11.5, color: 'var(--text-secondary)' }}>{r.email || '—'}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 700 }}>{fmtDate(r.rdv_date)}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 700, color: 'var(--accent)' }}>{fmtTime(r.rdv_time)}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 700 }}>{r.advisor_name || '—'}</td>
                    <td style={{ padding: '8px 12px', fontSize: 11.5 }}>{r.mode === 'virtuel' ? 'Virtuel' : r.mode === 'telephonique' ? 'Téléphonique' : '—'}</td>
                    <td style={{ padding: '8px 12px' }}>{badge(r.statut)}{due && <div style={{ fontSize: 10, color: '#f97316', fontWeight: 800, marginTop: 2 }}>À rappeler : {fmtDT(r.followup_at)}</div>}</td>
                    <td style={{ padding: '8px 12px', fontSize: 11.5, color: 'var(--text-secondary)', maxWidth: 180 }}>
                      {r.comments && <div>{r.comments}</div>}
                      {r.retour && <div style={{ color: '#0369a1' }}>{r.retour}</div>}
                      {r.followup_at && (r.statut === 'injoignable' || r.statut === 'rdv2') && (
                        <div style={{ color: '#f97316', fontWeight: 700 }}>
                          {r.statut === 'injoignable' ? 'Agent rappelle' : 'Client rappelle'} : {fmtDT(r.followup_at)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      <button type="button" className="btn btn-secondary btn-sm" style={{ marginRight: 4 }} onClick={() => setEdit({ ...r })} title="Modifier">Modifier</button>
                      <button type="button" className="btn btn-secondary btn-sm" style={{ marginRight: 4 }} disabled={!collected} onClick={() => setDetail(r)} title="Données collectées (fiche d'appel)">Données</button>
                      <button type="button" className="btn btn-secondary btn-sm" style={{ marginRight: 4 }} onClick={() => markRetour(r, 'rencontre')} title="OK — rencontre effectuée">OK</button>
                      <button type="button" className="btn btn-secondary btn-sm" style={{ marginRight: 4 }} onClick={() => markRetour(r, 'no_show')} title="No show — pas venu">No show</button>
                      <button type="button" className="btn btn-secondary btn-sm" style={{ marginRight: 4 }} onClick={() => markRetour(r, 'injoignable')} title="Injoignable → agent rappelle">Injoignable</button>
                      <button type="button" className="btn btn-secondary btn-sm" style={{ marginRight: 4 }} onClick={() => markRetour(r, 'rdv2')} title="2ᵉ RDV → client rappelle">2ᵉ RDV</button>
                      <button type="button" className="btn btn-secondary btn-sm" style={{ marginRight: 4 }} onClick={() => markRetour(r, 'refus')} title="Refus">Refus</button>
                      <button type="button" className="btn btn-secondary btn-sm" style={{ marginRight: 4 }} onClick={() => markRetour(r, 'reporte')} title="RDV reporté — nouvelle date/heure">Reporté</button>
                      <button type="button" className="btn btn-secondary btn-sm" style={{ marginRight: 4 }} onClick={() => cancelError(r)} title="Annuler (validation par erreur)">Annuler</button>
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => remove(r)} title="Supprimer">Supprimer</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* Données collectées — fiche d'appel complète (réponses du script) */}
      {detail && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => e.target === e.currentTarget && setDetail(null)}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius)', padding: 22, width: 560, maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto', boxShadow: 'var(--shadow-lg)' }}>
            <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 4 }}>Données collectées — {[detail.client_first, detail.client_last].filter(Boolean).join(' ')}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginBottom: 12 }}>
              Appel : {detail.created_at ? fmtDT(detail.created_at) : '—'} · Agent : {detail.agent_name || '—'}
            </div>
            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14, fontSize: 12.5, lineHeight: 1.8 }}>
              {(collectedData(detail) || 'Aucune donnée collectée sur la fiche de ce prospect.').split(' | ').map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setDetail(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit / add modal */}
      {edit && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={e => { if (e.target === e.currentTarget) setEdit(null); }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius)', padding: 24, width: 500, maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)' }}
            onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 18px', fontSize: 17, fontWeight: 800 }}>{edit.id ? 'Modifier le rendez-vous' : '+ Nouveau rendez-vous'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-secondary)' }}>Prénom<input style={inputStyle} value={edit.client_first || ''} onChange={e => setEdit({ ...edit, client_first: e.target.value })} /></label>
              <label style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-secondary)' }}>Nom<input style={inputStyle} value={edit.client_last || ''} onChange={e => setEdit({ ...edit, client_last: e.target.value })} /></label>
              <label style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-secondary)' }}>Téléphone<input style={inputStyle} value={edit.phone || ''} onChange={e => setEdit({ ...edit, phone: e.target.value })} /></label>
              <label style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-secondary)' }}>Courriel<input style={inputStyle} value={edit.email || ''} onChange={e => setEdit({ ...edit, email: e.target.value })} /></label>
              <label style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-secondary)' }}>Date RDV<input type="date" style={inputStyle} value={edit.rdv_date ? String(edit.rdv_date).slice(0, 10) : ''} onChange={e => setEdit({ ...edit, rdv_date: e.target.value })} /></label>
              <label style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-secondary)' }}>Heure<input type="time" style={inputStyle} value={edit.rdv_time ? String(edit.rdv_time).slice(0, 5) : ''} onChange={e => setEdit({ ...edit, rdv_time: e.target.value })} /></label>
              <label style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-secondary)' }}>Client
                <select style={inputStyle} value={edit.advisor_id || ''} onChange={e => setEdit({ ...edit, advisor_id: e.target.value })}>
                  <option value="">— Choisir —</option>
                  {advisors.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-secondary)' }}>Mode
                <select style={inputStyle} value={edit.mode || ''} onChange={e => setEdit({ ...edit, mode: e.target.value })}>
                  <option value="telephonique">Téléphonique</option>
                  <option value="virtuel">Virtuel (Zoom/Teams)</option>
                </select>
              </label>
              <label style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-secondary)' }}>Statut
                <select style={inputStyle} value={edit.statut || 'nouveau'} onChange={e => setEdit({ ...edit, statut: e.target.value })}>
                  {Object.entries(STATUTS).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-secondary)' }}>Ligne 2<input style={inputStyle} value={edit.ligne2 || ''} onChange={e => setEdit({ ...edit, ligne2: e.target.value })} /></label>
              <label style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-secondary)' }}>Rappel (AAAA-MM-JJ HH:MM, heure du Québec)
                <input style={inputStyle} placeholder="ex : 2026-10-15 14:30" value={edit.followup_at ? String(edit.followup_at).slice(0, 16).replace('T', ' ') : ''} onChange={e => setEdit({ ...edit, followup_at: e.target.value })} />
              </label>
              <label style={{ gridColumn: '1 / -1', fontSize: 11, fontWeight: 800, color: 'var(--text-secondary)' }}>Commentaires (agent)<input style={inputStyle} value={edit.comments || ''} onChange={e => setEdit({ ...edit, comments: e.target.value })} /></label>
              <label style={{ gridColumn: '1 / -1', fontSize: 11, fontWeight: 800, color: '#0369a1' }}>Retour du client<input style={inputStyle} value={edit.retour || ''} onChange={e => setEdit({ ...edit, retour: e.target.value })} /></label>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <button type="button" className="btn btn-primary" style={{ flex: 1 }} onClick={saveEdit} disabled={saving}>{saving ? 'Enregistrement…' : edit.id ? 'Enregistrer' : 'Ajouter'}</button>
              <button type="button" className="btn btn-secondary" onClick={() => setEdit(null)}>Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
